import { EntityRoutes } from 'routes/constants';
import strings from 'strings';
import { FeatureFlags } from 'UI/constants/featureFlags';
import {
  EMAIL_WITH_VALIDATION_COLUMN,
  getEmailValidationStatusColumn,
  KEYWORDS_COLUMN,
  LEGACY_EMAIL_COLUMN,
  OPT_OUT_COLUMN,
  PhoneColumns
} from 'UI/constants/sharedColumns';
import { hasFeatureFlag } from 'UI/utils';

const LEGACY_PERSONAL_EMAIL_COLUMN = {
  name: 'personal_email',
  label: 'Personal Email',
  options: {
    sort: true,
    display: false
  }
};

const PERSONAL_EMAIL_WITH_VALIDATION_COLUMN = {
  name: 'secondaryEmail',
  label: 'Personal Email',
  options: {
    sort: true,
    display: true,
    renderer: {
      id: 'secondaryEmail',
      config: {
        prefix: 'personal'
      }
    }
  }
};

const EMAIL_VALIDATION_STATUS_COLUMN = getEmailValidationStatusColumn({
  featureFlagToHideColumn: FeatureFlags.EmailWithValidationColumnCandidates
});

const FINAL_EMAIL_COLUMNS = hasFeatureFlag(FeatureFlags.EmailWithValidationColumnCandidates)
  ? [EMAIL_WITH_VALIDATION_COLUMN, PERSONAL_EMAIL_WITH_VALIDATION_COLUMN]
  : [LEGACY_EMAIL_COLUMN, LEGACY_PERSONAL_EMAIL_COLUMN, EMAIL_VALIDATION_STATUS_COLUMN];

export const ColumnsDefinitions = [
  {
    name: 'id',
    options: {
      filter: false,
      display: 'excluded'
    }
  },
  {
    name: 'status',
    label: 'Status',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'fastChipBuilder',
        config: {
          labelKey: 'title',
          baseClass: 'chip-candidate-status',
          variant: 'outlined'
        }
      }
    }
  },
  {
    name: 'type',
    label: 'Type',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'fastChipBuilder',
        config: {
          labelKey: 'title',
          baseClass: 'chip-candidate-type'
        }
      }
    }
  },
  {
    name: 'full_name',
    label: 'Full Name',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'linkBuilder',
        config: {
          route: EntityRoutes.CandidateProfile,
          idKey: 'id',
          textKey: 'full_name',
          enableInTabRedirect: true
        }
      }
    }
  },
  {
    name: 'nickname',
    label: 'Nickname',
    options: {
      sort: true,
      display: false
    }
  },
  {
    name: 'title',
    label: 'Title',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'functional_title',
    label: strings.shared.fields.jobFunction,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'current_company',
    label: 'Company',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'specialty_title',
    label: 'Specialty: Subspecialty',
    options: {
      sort: true,
      display: false
    }
  },
  {
    name: 'specialty',
    label: 'Specialty',
    options: {
      sort: true,
      display: false
    }
  },
  {
    name: 'industry',
    label: 'Industry',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'subspecialty',
    label: 'Subspecialty',
    options: {
      sort: true,
      display: false
    }
  },
  {
    name: 'location',
    label: 'Location',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'state',
    label: 'State',
    options: {
      sort: true,
      display: false
    }
  },
  {
    name: 'city',
    label: 'City',
    options: {
      sort: true,
      display: false
    }
  },
  {
    name: 'created_at',
    label: 'Added Date',
    options: {
      sort: true,
      display: true,
      renderer: 'date'
    }
  },
  {
    name: 'last_activity_date',
    label: 'Last Activity on',
    options: {
      sort: true,
      display: false,
      renderer: 'date'
    }
  },
  {
    name: 'recruiter',
    label: 'Recruiter',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'coach',
    label: 'Team',
    options: {
      sort: true,
      display: false
    }
  },
  OPT_OUT_COLUMN,
  ...FINAL_EMAIL_COLUMNS,
  PhoneColumns.PHONE_LEGACY,
  PhoneColumns.OTHER_PHONE_LEGACY,
  {
    name: 'no_brainer_salary',
    label: 'High Salary',
    options: {
      sort: true,
      display: true,
      renderer: 'currency'
    }
  },
  {
    name: 'good_salary',
    label: 'Ideal Salary',
    options: {
      sort: true,
      display: true,
      renderer: 'currency'
    }
  },
  {
    name: 'minimum_salary',
    label: 'Low Salary',
    options: {
      sort: true,
      display: true,
      renderer: 'currency'
    }
  },

  {
    name: 'work_type',
    label: 'Work Preference',
    options: {
      sort: true,
      display: false
    }
  },
  {
    name: 'opt_out_date',
    label: 'Opt Out Date',
    options: {
      renderer: 'date'
    }
  },
  {
    name: 'opt_out_reason',
    label: 'Opt Out Reason',
    options: {
      renderer: 'default'
    }
  },
  {
    name: 'last_activity_recruiter',
    label: 'Last Activity by',
    options: {
      sort: false,
      renderer: 'default'
    }
  },
  {
    name: 'last_activity_title',
    label: 'Last Activity',
    options: {
      sort: false,
      renderer: 'default'
    }
  },
  KEYWORDS_COLUMN
];

const PhoneFilter = hasFeatureFlag(FeatureFlags.MultiplePhonesColumn) ? 'partialPhone' : 'phone';
const showActivityFilters = hasFeatureFlag(FeatureFlags.FilterByCandidateActivities);

const showPersonalEmailValidation = hasFeatureFlag(
  FeatureFlags.EmailWithValidationColumnCandidates
);

export const FiltersGroups = [
  {
    id: 'general',
    name: 'Candidate',
    filters: [
      'statuses',
      'types',
      PhoneFilter,
      'workTypes',
      'keywordsTags',
      'textSearch',
      'optOut',
      'emailVerdicts',
      ...(showPersonalEmailValidation ? ['personalEmailVerdicts'] : [])
    ]
  },
  ...(showActivityFilters
    ? [
        {
          id: 'activity',
          name: 'Activity',
          filters: ['activityCondition', 'activityLogTypeIds', 'activityPeriodIds']
        }
      ]
    : []),
  {
    id: 'industries',
    name: 'Industries',
    filters: ['industries', 'specialties', 'subspecialties', 'positions', 'title']
  },
  { id: 'team', name: 'Team', filters: ['coaches', 'recruiters'] },
  {
    id: 'location',
    name: 'Location',
    filters: [
      'countries',
      'states',
      'cities',
      'cityRadius',
      'zips',
      'radius',
      'openToRelocation',
      'relocationDestinations'
    ]
  }
];

export const VirtualProps = [
  {
    propName: 'full_name',
    attributes: ['id', 'full_name']
  },
  {
    propName: 'phoneNumbers',
    attributes: ['phone', 'phones']
  },
  {
    propName: 'mainEmail',
    attributes: ['email', 'email_verdict']
  },
  {
    propName: 'secondaryEmail',
    attributes: ['personal_email', 'personal_email_verdict']
  }
];

export const PeriodColumnBySection = {
  0: 'created_at',
  1: 'last_activity_date'
};

export const CreatedAtFilter = { id: 0, title: 'Added Date', column: 'created_at' };
export const LastActivityDateFilter = {
  id: 1,
  title: 'Last Activity Date',
  column: 'last_activity_date'
};

export const DateFilters = [CreatedAtFilter, LastActivityDateFilter];
